import React from 'react'
import { StackView } from '@planningcenter/tapestry-react'
import {
  Code,
  CodeBlock,
  Link,
  List,
  PageHeading,
  Heading,
  Text,
} from '../../../shared/typography'

const page = () => {
  return (
    <StackView axis="vertical" spacing={2}>
      <PageHeading>Add-ons CLI</PageHeading>

      <Text>
        The <Code>@planningcenter/add-ons-cli</Code> tool helps you generate a
        skeleton add-on and is what you will use to configure and upload your
        code to Planning Center.
      </Text>

      <Text>
        Once installed with npm, the subcommands are to be used like this:
      </Text>

      <List>
        <List.Item>
          <Text>
            <Code>planningcenter-add-ons -V</Code>: Show the current version of
            the tool installed.
          </Text>
        </List.Item>
        <List.Item>
          <Text>
            <Code>planningcenter-add-ons create {'<name>'}</Code>: Creates a new
            directory with sample code for your new Add-on.
          </Text>
        </List.Item>
        <List.Item>
          <Text>
            <Code>planningcenter-add-ons update</Code>: Bundles and uploads
            add-on code to Planning Center.
          </Text>
        </List.Item>
        <List.Item>
          <Text>
            <Code>planningcenter-add-ons watch</Code>: Watches the files in the
            add-on directory and runs <Code>update</Code> when they change.
          </Text>
        </List.Item>
        <List.Item>
          <Text>
            <Code>
              planningcenter-add-ons promote {'<from-env>'} {'<to-env>'}
            </Code>
            : Copies bundled JavaScript from one environment to the next, e.g.
            from <Code>testing</Code> to <Code>beta</Code>, or from{' '}
            <Code>beta</Code> to <Code>production</Code>.
          </Text>
        </List.Item>
      </List>

      <Heading anchored id="config" size={1} level={2}>
        <Code>config.yaml</Code>
      </Heading>

      <Text>
        Your add-on directory generated by the CLI tool contains a{' '}
        <Code>config.yaml</Code> file that looks like this:
      </Text>

      <CodeBlock language="yaml">
        {`
testing_oauth_config: &testing_oauth_config
  authorize_url: http://myapp.local/oauth/authorize
  token_url: http://myapp.local/oauth/token
  redirect_url: http://myapp.local/oauth/complete
  identifier: "{{ TESTING_INTEGRATOR_OAUTH_IDENTIFIER }}"
  secret: "{{ TESTING_INTEGRATOR_OAUTH_SECRET }}"
  scopes: []

production_oauth_config: &production_oauth_config
  authorize_url: https://example.com/oauth/authorize
  token_url: https://example.com/oauth/token
  redirect_url: https://example.com/oauth/complete
  identifier: "{{ PRODUCTION_INTEGRATOR_OAUTH_IDENTIFIER }}"
  secret: "{{ PRODUCTION_INTEGRATOR_OAUTH_SECRET }}"
  scopes: []

add_on:
  oauth_application_id: "300"
  environments:
    testing:
      integrator_oauth: *testing_oauth_config
    beta:
      integrator_oauth: *production_oauth_config
      ui_extensions:
        - people.list.send_message
      organization_ids: []
    production:
      integrator_oauth: *production_oauth_config
      ui_extensions:
        - people.list.send_message
  scopes:
    - people
  ui_extensions:
    people.list.send_message:
      title: Send text with Foo
      icon_url: data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMjAgMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMTAiIGN5PSIxMCIgcj0iOCIgc3Ryb2tlPSJoc2woMjY4LCA3MSUsIDI3JSkiIHN0cm9rZS13aWR0aD0iMiIgZmlsbD0ibm9uZSIvPjwvc3ZnPg==
      icon_dark_url: data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMjAgMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMTAiIGN5PSIxMCIgcj0iOCIgc3Ryb2tlPSJoc2woMjY4LCA3MCUsIDg4JSkiIHN0cm9rZS13aWR0aD0iMiIgZmlsbD0ibm9uZSIvPjwvc3ZnPg==
      component_version: "6"
`.trim()}
      </CodeBlock>

      <Text>The meaning of each key is described below:</Text>

      <List>
        <List.Item>
          <Text>
            <Code>oauth_application_id</Code>: This is the ID of the OAuth
            application record in Planning Center that houses your application
            configuration. You can manage this record here:{' '}
            <Link
              to="https://api.planningcenteronline.com/oauth/applications"
              external
            >
              https://api.planningcenteronline.com/oauth/applications
            </Link>
            . The value of this field should be the application ID you see in
            the URL when viewing your application. For example, if clicking
            "edit" on your OAuth application takes you to{' '}
            <Code>
              https://api.planningcenteronline.com/oauth/applications/42/edit
            </Code>
            , then the application ID is 42.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <Code>environments</Code>
          </Text>

          <List>
            <List.Item>
              <Text>
                <Code>testing</Code>, <Code>beta</Code>, and{' '}
                <Code>production</Code>
              </Text>

              <List>
                <List.Item>
                  <Text>
                    <Code>integrator_oauth</Code>: This section contains
                    settings so Planning Center can get an OAuth token from your
                    (the Integrator’s) API. The{' '}
                    <Code>authenticatedIntegratorFetch()</Code> function
                    utilizes the the OAuth token obtained with this
                    configuration.
                  </Text>

                  <List>
                    <List.Item>
                      <Text>
                        <Code>authorize_url</Code>: OAuth will be initiated at
                        this URL where the user will click "Allow" or similar.
                        This is a URL on your server that gives the user the
                        chance to see that Planning Center will be accessing the
                        data on your server on their behalf.
                      </Text>
                    </List.Item>

                    <List.Item>
                      <Text>
                        <Code>token_url</Code>: The OAuth token will be obtained
                        from this URL
                      </Text>
                    </List.Item>

                    <List.Item>
                      <Text>
                        <Code>redirect_url</Code>: The URL where Planning Center
                        will redirect after the user authorizes your add-on to
                        access Planning Center data. (This must match one of the
                        "Authorization callback URLs" on the OAuth Application
                        configuration{' '}
                        <Link
                          to="https://api.planningcenteronline.com/oauth/applications"
                          external
                        >
                          here
                        </Link>
                        .)
                      </Text>
                    </List.Item>

                    <List.Item>
                      <Text>
                        <Code>identifier</Code>: This is the OAuth application
                        identifier
                      </Text>
                    </List.Item>

                    <List.Item>
                      <Text>
                        <Code>secret</Code>: This is the OAuth application
                        secret
                      </Text>
                    </List.Item>

                    <List.Item>
                      <Text>
                        <Code>scopes</Code>: This is the list of scopes Planning
                        Center will request from your OAuth server, as a YAML
                        array
                      </Text>
                    </List.Item>
                  </List>
                </List.Item>

                <List.Item>
                  <Text>
                    <Code>ui_extensions</Code>: This is a YAML array of UI
                    Extension names, e.g. <Code>people.list.send_message</Code>.
                    The configuration for these UI Extensions is later in the
                    config file. This is just a list of the names. This would
                    allow you to hide new UI Extension from your production or
                    beta environment while it is in early development.
                  </Text>
                </List.Item>
              </List>
            </List.Item>

            <List.Item>
              <Text>
                <Code>beta</Code>-only config
              </Text>

              <List>
                <List.Item>
                  <Text>
                    <Code>organization_ids</Code>: This is a YAML array of
                    Organization IDs that control which organizations see your
                    "beta" add-on. You can put up to 20 IDs in this list.
                  </Text>
                </List.Item>
              </List>
            </List.Item>
          </List>
        </List.Item>

        <List.Item>
          <Text>
            <Code>scopes</Code>: This is a list of OAuth scopes for your add-on.
            The API token generated for your add-on automatically by{' '}
            <Code>authenticatedFetch()</Code> will have these scopes. Only use
            the OAuth scopes you absolutely need. These are Planning Center
            scopes, e.g. <Code>people</Code>, <Code>services</Code>,{' '}
            <Code>registrations</Code>, etc.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <Code>ui_extensions</Code>
          </Text>

          <List>
            <List.Item>
              <Text>
                <em>
                  <b>inserstion point</b>
                </em>
                : This is the name of an insertion point where your UI Extension
                will appear. This name must exactly match the filename of your
                UI Extension, e.g. for the insertion point{' '}
                <Code>people.list.send_message</Code>, the filename must be{' '}
                <Code>people.list.send_message.jsx</Code>.
              </Text>

              <List>
                <List.Item>
                  <Text>
                    <Code>title</Code>: This text will appear at the insertion
                    point and be an actionable link, button, or tab for your
                    user to choose.
                  </Text>
                </List.Item>

                <List.Item>
                  <Text>
                    <Code>icon_url</Code>: This is an icon that may or may not
                    appear next to the title. This depends on how the insertion
                    point is designed in the UI and will vary.
                  </Text>
                </List.Item>

                <List.Item>
                  <Text>
                    <Code>icon_dark_url</Code>: If the user prefers dark mode,
                    we'll use this icon instead.
                  </Text>
                </List.Item>

                <List.Item>
                  <Text>
                    <Code>component_version</Code>: Specifies the component
                    version your UI Extension uses, allowing you to opt-in to
                    breaking component changes once your extension has been
                    updated. Default: <Code>"5"</Code>
                  </Text>
                </List.Item>
              </List>
            </List.Item>
          </List>
        </List.Item>
      </List>
    </StackView>
  )
}

export default page
